import { inject, injectable } from 'inversify';
import { ApolloClient } from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import {
  CacheTypenames,
  IStatusbarService,
  IStatusbarEntry, StatusbarAlignment, IStatusbarItem,
  STATUSBAR_ITEM_ID,
} from '@workbench-stack/core';
import { ClientTypes as CommonTypes } from '@common-stack/client-react';
import { StatusbarDocument, statusbarItemFragmentDoc, StatusbarItemDocument } from '@workbench-stack/components/lib/editors';
import * as _ from 'lodash';

@injectable()
export class StatusbarService implements IStatusbarService {
    constructor(
        @inject(CommonTypes.ApolloClient)
        protected client: ApolloClient<any>,
        @inject(CommonTypes.InMemoryCache)
        protected cache: InMemoryCache,
    ) {
    }

    public addEntry(entry: IStatusbarEntry, id: string, name: string, alignment: StatusbarAlignment, priority?: number) {
        let existItems: any = {};
        let statusbar_input;
        let itemTypeId;
        switch (id) {
            case CacheTypenames.Terminal:
                itemTypeId = STATUSBAR_ITEM_ID.TERMINAL;
                break;
            case CacheTypenames.SelectionStatus:
                itemTypeId = STATUSBAR_ITEM_ID.SELECTION_STATUS;
                break;
            case CacheTypenames.Indentation:
                itemTypeId = STATUSBAR_ITEM_ID.INDENTATION_STATUS;
                break;
            case CacheTypenames.EncodingStatus:
                itemTypeId = STATUSBAR_ITEM_ID.ENCODING_STATUS;
                break;
            case CacheTypenames.EndOfLineSequence:
                itemTypeId = STATUSBAR_ITEM_ID.EOL_STATUS;
                break;
            case CacheTypenames.LanguageMode:
                itemTypeId = STATUSBAR_ITEM_ID.LANGUAGE_MODE_STATUS;
                break;
            case CacheTypenames.SyncStatus:
                itemTypeId = STATUSBAR_ITEM_ID.SYNC_STATUS;
                break;
            case CacheTypenames.BranchStatus:
                itemTypeId = STATUSBAR_ITEM_ID.BRANCH_STATUS;
                break;
        }
        const newItem = {
            id: id,
            name: name,
            entry: {
                showTabsButtons: entry.showTabsButtons,
                showIcons: entry.showIcons,
                showStatusbar: entry.showStatusbar,
                text: entry.text,
                ariaLabel: entry.ariaLabel,
                tooltip: entry.tooltip,
                color: entry.color,
                backgroundColor: entry.backgroundColor,
                command: entry.command,
                arguments: entry.arguments,
                showBeak: entry.showBeak,
                __typename: CacheTypenames.StatubarEntry,
            },
            alignment: alignment,
            priority: priority || 1,
            typeId: itemTypeId,
            __typename: id,
        };

        try {
            existItems = this.client.readQuery({
                query: StatusbarDocument,
            });
            if (existItems.statusbar) {
                const index = _.findIndex(existItems.statusbar.items, (item: IStatusbarItem) => item.id === id);
                if (index !== -1) {
                    let newItems = existItems.statusbar.items.slice(0);
                    newItems.splice(index, 1, {...newItem, priority: priority ? priority : newItems.length + 1});
                    statusbar_input = {
                        statusbar: {
                            items: newItems,
                            __typename: CacheTypenames.Statusbar,
                        },
                    };
                } else {
                    statusbar_input = {
                        statusbar: {
                            items: [
                                ...existItems.statusbar.items,
                                { ...newItem, priority: priority ? priority : existItems.statusbar.items.length + 1 }],
                            __typename: CacheTypenames.Statusbar,
                        }
                        ,
                    };
                }
            }
        } catch (e) {
            statusbar_input = {
                statusbar: {
                    items: [newItem],
                    __typename: CacheTypenames.Statusbar,
                },
            };
        }
        this.client.writeQuery({
            query: StatusbarDocument,
            data: statusbar_input,
        });
        return true;
    }

    public removeStatusbarEntry(id: string) {
        let items: any = {};
        try {
            items = this.client.readQuery({
                query: StatusbarDocument,
            });
            if (items.statusbar.items) {
                let statusbarArr = items.statusbar.items.slice();
                const index = statusbarArr.findIndex(o => o.id === id);
                if (index !== -1) {
                    statusbarArr.splice(index, 1);
                    this.client.writeQuery({
                        query: StatusbarDocument,
                        data: {
                            statusbar: {
                                items: [...statusbarArr],
                                __typename: CacheTypenames.StatusbarItem,
                            },
                            __typename: CacheTypenames.Statusbar,
                        },
                    });
                }
                return true;
            }
        } catch (e) {
            console.error('status bar item not found to remove', e);
            return false;
        }
    }

    public updateStatusbarEntry(entry: IStatusbarEntry, id: string, alignment: StatusbarAlignment, priority?: number) {
        let item: any = {};
        try {
            item = this.client.readFragment({
                fragment: statusbarItemFragmentDoc,
                id: id,
            });
            if (item) {
                const data = {
                    ...item,
                    alignment,
                    priority,
                    entry: {...item.entry, ...entry},
                };

                this.client.writeFragment({
                    fragment: statusbarItemFragmentDoc,
                    data: data,
                    id: id,
                });
            }
            return true;
        } catch (e) {
            console.error('update entry mutation failed', e);
            return false;
        }
    }

    public setStatusMessage(id: string, message: string) {
        try {
            const { statusbarItem: item } = this.client.readQuery({
                query: StatusbarItemDocument,
                variables: { id },
            });
            if (item) {
                const data = {
                    ...item,
                    entry: { ...item.entry, text: message },
                };
                this.client.writeQuery({
                    query: StatusbarItemDocument,
                    variables: { id },
                    data: { statusbarItem: data },
                });
            }
            return 'message updated';
        } catch (e) {
            return e.toString();
        }
    }
}
